// ═══════════════════════════════════════════════════════════════════════════
// COLLAB-LOCK-COMMAND — collab soft-lock connection store (S-COLLAB-LOCK)
// ═══════════════════════════════════════════════════════════════════════════
//
// The zustand slice that drives the LOCK/UNLOCK command-line surface. Mirrors
// the exact architectural pattern of `connectionStore.ts` (the agent-bridge
// connection store): SEPARATE from `editorStore` on purpose — connecting /
// disconnecting a collaboration session, or a soft lock changing hands, MUST
// NOT touch the drawing's undo/redo history (a lock is advisory session
// state, not a document mutation). DEFAULT-OFF: the store starts
// `disconnected` with NO client; `connect()` is the ONLY thing that builds a
// `CollabLockClient` + opens a socket.
//
// THIS PR SHIPS NO CONNECT UI (Category B — no ConnectDialog / pairing flow).
// `connect()` is called directly with an explicit `{token, documentId,
// identity}` — tests (and, later, the deferred S-COLLAB-UI dialog) supply
// these. `clientFactory` is DI-injectable (default the real
// `CollabLockClient`) exactly like `connectionStore`'s `initAgentBridge`
// injection, so a test drives a MOCK socket with no real network.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports zustand + the collab client/types only.
// Does NOT import `editorStore` — the CLI-intercept wiring in
// `editorStore.ts` imports THIS store (one-directional), never the reverse,
// so there is no cycle.
// ═══════════════════════════════════════════════════════════════════════════

import { create } from "zustand";

import {
  CollabLockClient,
  COLLAB_LOCK_WS_URL,
  type CollabLockClientDeps,
  type CollabLockConnectionStatus,
} from "../collab/collabLockClient";
import type {
  LockProposalOutcome,
  LockScope,
  LockState,
  ParticipantIdentity,
} from "../collab/collabLockTypes";

/** The params `connect()` needs. `url` defaults to the hardcoded loopback collab target when omitted. */
export type CollabLockConnectOptions = {
  url?: string;
  token: string;
  documentId: string;
  identity: ParticipantIdentity;
};

/**
 * The DI factory `connect` uses to build the client. Defaults to the real
 * `CollabLockClient`; a test injects one that wires a mock socket so no real
 * network opens. `deps` carries the store-bound `onStatusChange`/
 * `onLocksSync` callbacks (built by `connect` itself) — mirrors
 * `AgentBridgeHandleFactory`'s shape one level down (no separate "mount"
 * module is needed here: unlike agent-bridge, this client has NO
 * editorStore coupling to compose in).
 */
export type CollabLockClientFactory = (deps: CollabLockClientDeps) => CollabLockClient;

const defaultClientFactory: CollabLockClientFactory = (deps) => new CollabLockClient(deps);

/** The collab-lock store shape (state + the lifecycle/lock actions the CLI intercept calls). */
export interface CollabLockStoreState {
  /** The current coarse connection state. */
  status: CollabLockConnectionStatus;
  /**
   * The LIVE lock map, mirrored from the client on every welcome / lock-update
   * / reset. Keyed by `lockScopeKey`. THIS is the map `collabLockVeto.ts`
   * reads to fail-closed-reject a conflicting edit — empty while
   * disconnected, so a normal (non-collab) session's every-day edit is never
   * affected (zero-overhead no-op).
   */
  locks: ReadonlyMap<string, LockState>;
  /** The server-minted participant id for THIS tab, or `null` before pairing / after a reset. */
  selfParticipantId: string | null;
  /**
   * The live client handle (`null` until `connect()`). NOT rendered by any
   * UI in this PR — held so `disconnect()` can tear it down and exposed for
   * tests / the future S-COLLAB-UI slice.
   */
  client: CollabLockClient | null;
  /**
   * Open the loopback WS + pair. Builds a fresh client (tearing down any
   * existing one first — this store does not attempt the agent-bridge
   * connection store's idempotent-reuse optimisation; a repeat `connect()`
   * always rebuilds, which is correct and simple for the CLI-driven
   * connect flow this PR ships) and wires the client's `onStatusChange` /
   * `onLocksSync` callbacks into `set`. `clientFactory` is DI (default the
   * real `CollabLockClient`) so a test never opens a real socket.
   */
  connect: (options: CollabLockConnectOptions, clientFactory?: CollabLockClientFactory) => void;
  /** Tear down the socket + the client → `disconnected`, empty `locks`, null `selfParticipantId`. */
  disconnect: () => void;
  /** Delegates to the live client's `requestLocks`. Rejects if not connected (fail-closed — never a silent no-op). */
  requestLocks: (scopes: readonly LockScope[]) => Promise<Map<string, LockProposalOutcome>>;
  /** Delegates to the live client's `releaseLocks`. Rejects if not connected. */
  releaseLocks: (scopes: readonly LockScope[]) => Promise<Map<string, boolean>>;
}

/**
 * The collab-lock store. SEPARATE from editorStore (mirrors `connectionStore`
 * — a lock/connection-state change must not touch drawing undo/redo).
 * Default `status='disconnected'`, no client, empty locks (INV-OFF).
 */
export const useCollabLockStore = create<CollabLockStoreState>((set, get) => ({
  status: "disconnected",
  locks: new Map(),
  selfParticipantId: null,
  client: null,

  connect: (options, clientFactory = defaultClientFactory) => {
    const existing = get().client;
    if (existing !== null) {
      // A repeat connect() always rebuilds — tear the old socket down first
      // so it never leaks (the CLI-driven connect flow this PR ships has no
      // reason to reuse a live socket across a param change).
      existing.disconnect();
    }
    const url = options.url ?? COLLAB_LOCK_WS_URL;
    let client: CollabLockClient;
    try {
      client = clientFactory({
        onStatusChange: (status) => set({ status }),
        onLocksSync: (locks, selfParticipantId) => set({ locks, selfParticipantId }),
      });
      set({ client, status: "connecting" });
      client.connect(url, {
        token: options.token,
        documentId: options.documentId,
        identity: options.identity,
      });
    } catch (err) {
      // A synchronous throw (the client's loopback-guard refusing a
      // non-loopback url, or a factory/construction failure) must NOT leave
      // the store wedged at `connecting`. Surface `error`; the raw detail is
      // `console.warn`'d for devs (this store carries no i18n error-key
      // taxonomy in this PR — Category B, no UI consumes it yet).
      console.warn("collab-lock: connect failed", err);
      set({ status: "error", client: null });
    }
  },

  disconnect: () => {
    const { client } = get();
    client?.disconnect();
    // Reset to the default state. Never touches editorStore.
    set({
      status: "disconnected",
      locks: new Map(),
      selfParticipantId: null,
      client: null,
    });
  },

  requestLocks: (scopes) => {
    const { client } = get();
    if (client === null) {
      return Promise.reject(
        new Error("collab-lock-store: requestLocks called while disconnected"),
      );
    }
    return client.requestLocks(scopes);
  },

  releaseLocks: (scopes) => {
    const { client } = get();
    if (client === null) {
      return Promise.reject(
        new Error("collab-lock-store: releaseLocks called while disconnected"),
      );
    }
    return client.releaseLocks(scopes);
  },
}));
